import { formatQty, formatTaka, type Milli, type Paisa } from '@khata/core';
import { digits, lang } from './i18n.js';

export { formatTaka, formatQty };

/** Money in the current language's digits. */
export const taka = (p: Paisa): string => digits(formatTaka(p));
export const signed = (p: Paisa): string => digits(formatTaka(p, { plus: true }));
export const qty = (m: Milli): string => digits(formatQty(m));
/** Short money for chart labels: 56k, 4.3L, 1.2Cr (in the current language's digits). */
export function compact(p: Paisa): string {
  const taka = Math.abs(p) / 100;
  const sign = p < 0 ? '−' : '';
  let s: string;
  if (taka >= 1e7) s = (taka / 1e7).toFixed(taka >= 1e8 ? 0 : 1).replace(/\.0$/, '') + 'Cr';
  else if (taka >= 1e5) s = (taka / 1e5).toFixed(taka >= 1e6 ? 0 : 1).replace(/\.0$/, '') + 'L';
  else if (taka >= 1e3) s = Math.round(taka / 1e3) + 'k';
  else s = String(Math.round(taka));
  return sign + digits(s);
}

/** A plain count in the current language's digits. */
export const num = (n: number): string => digits(String(n));

const FORMATS: Record<
  string,
  Record<'short' | 'long' | 'month' | 'monthYear', Intl.DateTimeFormat>
> = {};

function formats(locale: string) {
  return (FORMATS[locale] ??= {
    short: new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }),
    long: new Intl.DateTimeFormat(locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }),
    month: new Intl.DateTimeFormat(locale, { month: 'short' }),
    monthYear: new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }),
  });
}

function locale(): string {
  return lang.value === 'bn' ? 'bn-BD' : 'en-GB';
}

function at(date: string): Date {
  return new Date(date + 'T00:00:00');
}

/** '2026-09-19' → '19 Sept' / '১৯ সেপ্টেম্বর' */
export const dShort = (date: string): string => formats(locale()).short.format(at(date));
/** '2026-09-19' → 'Friday, 19 September 2026' */
export const dLong = (date: string): string => formats(locale()).long.format(at(date));
/** '2026-09' → 'Sept' */
export const mShort = (month: string): string => formats(locale()).month.format(at(month + '-01'));
/** '2026-09' → 'September 2026' */
export const mLong = (month: string): string =>
  formats(locale()).monthYear.format(at(month + '-01'));

export function localToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** English-only helper kept for places that show a plain count with a word. Prefer i18n `count`. */
export const plural = (n: number, one: string, many = one + 's'): string =>
  `${num(n)} ${n === 1 ? one : many}`;
